import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { ArrowRight, ArrowLeft } from 'lucide-react';
import Header from '../components/Header';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const diagnosticQuestions = {
  'Evidence Integrity': [
    { id: 'ei1', question: 'Are system decisions clearly explainable to users?' },
    { id: 'ei2', question: 'Are failures and errors visible rather than silent?' },
    { id: 'ei3', question: 'Do users receive clear feedback on their actions?' },
    { id: 'ei4', question: 'Can you trace the full journey of a transaction or action?' }
  ],
  'Identity Credibility': [
    { id: 'ic1', question: 'Can users bypass critical validation steps?' },
    { id: 'ic2', question: 'Are authentication mechanisms consistently enforced?' },
    { id: 'ic3', question: 'Can users manipulate their permissions or access levels?' },
    { id: 'ic4', question: 'Are identity checks consistent across all entry points?' }
  ],
  'Operational Visibility': [
    { id: 'ov1', question: 'Are errors properly logged and surfaced?' },
    { id: 'ov2', question: 'Can you detect delays or performance inconsistencies?' },
    { id: 'ov3', question: 'Do you have visibility into failed transactions?' },
    { id: 'ov4', question: 'Are system anomalies detectable in real-time?' }
  ],
  'Control Architecture': [
    { id: 'ca1', question: 'Are safeguards layered across critical flows?' },
    { id: 'ca2', question: 'Are edge cases properly handled and tested?' },
    { id: 'ca3', question: 'Do you have rollback mechanisms for failed operations?' },
    { id: 'ca4', question: 'Are rate limits and abuse prevention in place?' }
  ]
};

const DiagnosticPage = () => {
  const { leadId } = useParams();
  const navigate = useNavigate();
  const [currentDomain, setCurrentDomain] = useState(0);
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(false);

  const domains = Object.keys(diagnosticQuestions);
  const currentDomainName = domains[currentDomain];
  const currentQuestions = diagnosticQuestions[currentDomainName];
  const progress = ((currentDomain + 1) / domains.length) * 100;

  const handleAnswer = (questionId, answer, riskLevel) => {
    setAnswers({
      ...answers,
      [questionId]: {
        domain: currentDomainName,
        question: currentQuestions.find(q => q.id === questionId).question,
        answer,
        risk_level: riskLevel
      }
    });
  };

  const handleNext = () => {
    if (currentDomain < domains.length - 1) {
      setCurrentDomain(currentDomain + 1);
    } else {
      handleSubmit();
    }
  };

  const handleBack = () => {
    if (currentDomain > 0) {
      setCurrentDomain(currentDomain - 1);
    }
  };

  const isCurrentDomainComplete = () => {
    return currentQuestions.every(q => answers[q.id]);
  };

  const isLastDomain = currentDomain === domains.length - 1;

  const getNextButtonLabel = () => {
    if (loading) return 'Analyzing...';
    if (isLastDomain) return 'Generate Report';
    return 'Next Domain';
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const answerArray = Object.values(answers);
      
      const diagnosticResponse = await axios.post(`${API}/diagnostics`, {
        lead_id: leadId,
        answers: answerArray
      });

      const diagnosticId = diagnosticResponse.data.id;

      await axios.post(`${API}/diagnostics/${diagnosticId}/analyze`);

      navigate(`/report/${diagnosticId}`);
    } catch (err) {
      console.error('Error submitting diagnostic:', err);
      alert('Failed to submit diagnostic. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0B0B0D] py-12 px-6">
      <Header />
      <div className="max-w-4xl mx-auto pt-20">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <span className="font-mono text-xs uppercase tracking-widest text-muted-foreground" data-testid="diagnostic-label">
              TRUST SIGNAL SCAN
            </span>
            <span className="font-mono text-xs text-muted-foreground" data-testid="progress-label">
              Domain {currentDomain + 1} of {domains.length}
            </span>
          </div>
          <div className="w-full bg-muted rounded-full h-2 overflow-hidden" data-testid="progress-bar">
            <motion.div
              className="h-full bg-[#FF6A00]"
              initial={{ width: 0 }}
              animate={{ width: `${progress}%` }}
              transition={{ duration: 0.3 }}
            />
          </div>
        </div>

        {/* Domain Card */}
        <motion.div
          key={currentDomain}
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -20 }}
          className="glass-card p-8 rounded-lg mb-8"
        >
          <h2 className="text-3xl font-display font-bold mb-6" data-testid="domain-title">
            {currentDomainName}
          </h2>

          <div className="space-y-6">
            {currentQuestions.map((q, idx) => (
              <div key={q.id} className="space-y-3" data-testid={`question-${idx}`}>
                <p className="text-lg font-medium" data-testid={`question-text-${idx}`}>
                  {idx + 1}. {q.question}
                </p>
                <div className="flex gap-2">
                  {['Yes', 'Partial', 'No'].map((option) => {
                    const riskMap = { 'Yes': 'Low', 'Partial': 'Medium', 'No': 'High' };
                    const isSelected = answers[q.id]?.answer === option;
                    return (
                      <button
                        key={option}
                        onClick={() => handleAnswer(q.id, option, riskMap[option])}
                        className={`px-6 py-2 rounded-md font-medium transition-all duration-200 ${
                          isSelected
                            ? 'bg-[#FF6A00] text-white'
                            : 'bg-muted text-muted-foreground hover:bg-muted/80'
                        }`}
                        data-testid={`answer-${idx}-${option.toLowerCase()}`}
                      >
                        {option}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Navigation */}
        <div className="flex justify-between items-center">
          <button
            onClick={handleBack}
            disabled={currentDomain === 0}
            className="flex items-center gap-2 px-6 py-3 bg-transparent border border-input text-foreground font-medium rounded-md hover:bg-accent hover:text-accent-foreground transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="back-button"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </button>

          <button
            onClick={handleNext}
            disabled={!isCurrentDomainComplete() || loading}
            className="flex items-center gap-2 px-6 py-3 bg-[#FF6A00] text-white font-medium rounded-md hover:bg-[#FF6A00]/90 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed shadow-[0_0_15px_rgba(255,106,0,0.3)]"
            data-testid="next-button"
          >
            {getNextButtonLabel()}
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default DiagnosticPage;
